import { type ReactNode } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import _, { keyBy, mapValues } from 'lodash';
import { type Dictionary } from '@bbkAdminUtils/utility-types';
import { toDictionary } from '@bbkAdminRedux/redux_utils';
import {
  type CreateLossAversionDto,
  OfferGroup,
  OfferObjective,
  OfferPresentation,
  type PublishState,
  type RestLossAversion,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import {
  findPrimaryCard,
  isLossAversion,
  isOffer,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  RestModalAction,
  type RestModalRequires,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type BBKRequestContextArgs } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { useSelectAllFunnels } from '@bbkAdminRedux/rtkq/funnels.slice';
import { type BrandOptionType } from '@bbkAdminRedux/rtkq/brands.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { type FunnelPlacements } from '@bbkAdminRedux/rtkq/placements.utils';
import { useGetProductLicense } from '@bbkAdminUtils/plans';

type GetOffersArgs = {
  appKey: string;
  brandId?: string;
} & BBKRequestContextArgs;

type GetLossAversionsArgs = {
  appKey: string;
  brandId?: string;
} & BBKRequestContextArgs;

type UpdateLossAversionArgs = {
  appKey: string;
  content: RestLossAversion;
  publish_state: PublishState;
  comment: string;
};

type CreateLossAversionArgs = {
  appKey: string;
  lossAversion: RestLossAversion | CreateLossAversionDto;
};

type UpdateOfferArgs = {
  appKey: string;
  content: RestOffer;
  publish_state: PublishState;
  comment: string;
};

type CreateOfferArgs = {
  appKey: string;
  offer: Omit<RestOffer, 'id'>;
};
type GetCtaTextArgs = {
  appKey: string;
  body: {
    offer_id: string;
    cta_id: string;
  }[];
};
type GetCtaTextResponse = {
  result: {
    offer_id: string;
    cta_id: string;
    text: string;
  }[];
};
export const offersAndLossAversionsSlice = bbkApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOffers: builder.query<RestOffer[], GetOffersArgs>({
      query: ({ appKey, brandId, context }) => ({
        url: `/api/v1/apps/${appKey}/offers`,
        params: {
          active_only: false,
          context,
          ...(brandId ? { brand_id: brandId } : {}),
        },
      }),
      transformResponse: (res: { collection: RestOffer[] }) => res.collection,
      providesTags: ['Offers'],
    }),
    getLossAversions: builder.query<RestLossAversion[], GetLossAversionsArgs>({
      query: ({ appKey, context, brandId }) => ({
        url: `/api/v1/apps/${appKey}/loss_aversions`,
        params: {
          active_only: false,
          context,
          ...(brandId ? { brand_id: brandId } : {}),
        },
      }),
      transformResponse: (res: { collection: RestLossAversion[] }) =>
        res.collection,
      providesTags: ['LossAversions'],
    }),
    createLossAversion: builder.mutation<
      RestLossAversion,
      CreateLossAversionArgs
    >({
      query: ({ appKey, lossAversion: body }) => ({
        url: `/api/v1/apps/${appKey}/loss_aversions`,
        method: 'POST',
        body: { ...body, groups: [OfferGroup.loss_version] },
      }),
      invalidatesTags: ['LossAversions'],
    }),
    updateLossAversion: builder.mutation<unknown, UpdateLossAversionArgs>({
      query: ({ appKey, ...body }) => ({
        url: `/api/v1/apps/${appKey}/loss_aversion_change_requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LossAversions'],
    }),
    createOffer: builder.mutation<RestOffer, CreateOfferArgs>({
      query: ({ appKey, offer: body }) => ({
        url: `/api/v1/apps/${appKey}/offers`,
        method: 'POST',
        body: {
          ...body,
          groups:
            body.objective === OfferObjective.churn_deflection
              ? [OfferGroup.offer]
              : [OfferGroup.lifecycle_offer],
        },
      }),
      invalidatesTags: ['Offers'],
    }),
    updateOffer: builder.mutation<unknown, UpdateOfferArgs>({
      query: ({ appKey, ...body }) => ({
        url: `/api/v1/apps/${appKey}/offer_change_requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Offers'],
    }),
    getCtaTextByOfferIdCtaId: builder.query<GetCtaTextResponse, GetCtaTextArgs>(
      {
        query: (args) => ({
          url: `/api/v2/apps/${args.appKey}/reports/offers/cta`,
          method: 'POST',
          body: args.body,
        }),
      }
    ),
  }),
});

export const useSelectAllOffers = (args: GetOffersArgs): RestOffer[] => {
  const { data } =
    offersAndLossAversionsSlice.endpoints.getOffers.useQuery(args);
  return useMemo(() => data ?? [], [data]);
};

export const useSelectAllChurnDeflectOffers = (args: GetOffersArgs) => {
  const offers = useSelectAllOffers(args);
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;

  return useMemo(
    () =>
      agsLicense || growthV2License
        ? offers.filter(
            (o) =>
              o.objective === OfferObjective.churn_deflection ||
              (o.objective === OfferObjective.retention &&
                (o.presentation === OfferPresentation.modal ||
                  o.presentation === OfferPresentation.card)) ||
              o.objective == null
          )
        : offers,
    [offers, agsLicense, growthV2License]
  );
};

export const useSelectActiveOffers = (args: GetOffersArgs) => {
  const offers = useSelectAllChurnDeflectOffers(args);

  return useMemo(() => offers.filter((o) => o.active), [offers]);
};

export const useSelectArchivedOffers = (args: GetOffersArgs) => {
  const offers = useSelectAllChurnDeflectOffers(args);

  return useMemo(() => offers.filter((o) => o.active === false), [offers]);
};

export const useSelectCompatibleFinalOffers = (args: GetOffersArgs) => {
  const offers = useSelectActiveOffers(args);

  return useMemo(
    () =>
      offers.filter((x) => {
        const primaryCard = findPrimaryCard(x);
        const requires = primaryCard?.requires as RestModalRequires;
        if (!primaryCard || !requires) return false;

        const actions = [
          ...(requires.other_actions ?? []).map((a) => a.action),
          ...(requires.generic_actions ?? []).map((a) => a.action),
          requires.forwards?.action,
        ];

        return actions.includes(RestModalAction.cancel_with_link);
      }),
    [offers]
  );
};

export const useSelectAllOffersEntities = (args: GetOffersArgs) => {
  const offers = useSelectAllOffers(args);

  return useMemo(() => toDictionary(offers), [offers]);
};

export const useSelectAllLossAversions = (args: GetLossAversionsArgs) => {
  const { data } =
    offersAndLossAversionsSlice.endpoints.getLossAversions.useQuery(args);

  return useMemo(() => data ?? [], [data]);
};

export const useSelectAllLossAversionsEntities = (
  args: GetLossAversionsArgs
) => {
  const lossAversions = useSelectAllLossAversions(args);

  return useMemo(() => toDictionary(lossAversions), [lossAversions]);
};

export const useSelectActiveLossAversions = (args: GetLossAversionsArgs) => {
  const lossAversions = useSelectAllLossAversions(args);

  return useMemo(
    () => lossAversions.filter((la) => la.active !== false),
    [lossAversions]
  );
};

export const useSelectArchivedLossAversions = (args: GetLossAversionsArgs) => {
  const lossAversions = useSelectAllLossAversions(args);

  return useMemo(
    () => lossAversions.filter((la) => la.active === false),
    [lossAversions]
  );
};

export const useSelectAllOffersAndLossAversions = (
  args: GetLossAversionsArgs
) => {
  const offers = useSelectAllChurnDeflectOffers(args);
  const lossAversions = useSelectAllLossAversions(args);

  return useMemo(() => [...lossAversions, ...offers], [lossAversions, offers]);
};

export const useSelectAllOffersAndLossAversionsEntities = (
  args: GetLossAversionsArgs
) => {
  const items = useSelectAllOffersAndLossAversions(args);

  return useMemo(() => toDictionary(items), [items]);
};

export const useSelectLossAversionCompatibleItems = (
  args: GetLossAversionsArgs
) => {
  const items = useSelectAllOffersAndLossAversions(args);
  return useMemo(() => items.filter(isLossAversion), [items]);
};

export const useSelectLossAversionCompatibleEntities = (
  args: GetLossAversionsArgs
): Dictionary<RestLossAversion> => {
  const items = useSelectLossAversionCompatibleItems(args);
  return useMemo(() => toDictionary(items), [items]);
};

export const useSelectOffersCompatibleItems = (args: GetLossAversionsArgs) => {
  const items = useSelectAllOffersAndLossAversions(args);
  return useMemo(() => items.filter(isOffer), [items]);
};

export const useSelectOffersCompatibleEntities = (
  args: GetLossAversionsArgs
): Dictionary<RestOffer> => {
  const items = useSelectOffersCompatibleItems(args);
  return useMemo(() => toDictionary(items), [items]);
};

export const useGetContentPlacementBrandList = (
  content: RestOffer | RestLossAversion,
  placementsMap?: Dictionary<FunnelPlacements>
): { label: ReactNode; value: string }[] => {
  // Returns the list of brand the content is used

  const isMultiBrand = useFeatureFlags(FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED);

  const appKey = useSelector(selectAppId);
  const allFunnels = useSelectAllFunnels({ appKey });
  const allBrands = useGetBrandOptions({ appKey });

  return useMemo(() => {
    if (!isMultiBrand || _.isEmpty(allBrands)) {
      return [];
    }
    const funnelBrandIdMap = mapValues(keyBy(allFunnels, 'id'), 'brand_id');

    const allBrandsMap = keyBy(allBrands, 'value');
    const allPlacements =
      placementsMap &&
      Object.values(placementsMap).flatMap((p) => p?.placements);
    const brandList = new Set();
    const funnelIds = (
      placementsMap
        ? allPlacements?.filter((p) => p?.content_id === content.id)
        : content.placements
    )
      ?.map((p) => p?.funnel_id)
      .filter(Boolean);

    funnelIds?.forEach((funnelId) => {
      brandList.add(funnelBrandIdMap[funnelId] ?? DEFAULT_BRAND_ID);
    });

    return Array.from(brandList)
      .filter((b) => allBrandsMap[b as string] !== undefined)
      .map((b) => {
        const brandData = allBrandsMap[b as string] as BrandOptionType;
        return {
          label: brandData.label,
          value: brandData.value,
        };
      });
  }, [
    isMultiBrand,
    allFunnels,
    allBrands,
    placementsMap,
    content.placements,
    content.id,
  ]);
};
